import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Autocomplete,Button,Stack,TextField,Typography } from "@mui/material";
import Grid from '@mui/material/Grid';
import Pagination from '@mui/material/Pagination';
import { useQueries,useQuery } from "@tanstack/react-query";
import { useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AttToken from "../../helpers/attToken";
import { trackEventMatomo } from "../../helpers/matomo";
import Checklists from "../../service/Checklists";
import ServiceTypes from "../../service/ServiceTypes";
import { ChecklistType,formsResponse } from "../../types/checklist/types";
import { indexType } from "../../types/helpers/types";
import { types } from "../../types/services/types";
import CardFormulario from "./CardFormulario";
import CardFormularioSkeletonGrid from "./CardFormularioSkeletonGrid";
import NovoFormulario from "./Modal/NovoFormulario";

const tiposServicoService = new ServiceTypes();
const formularioService = new Checklists();

export default function ListaFormulario() {
  const { t } = useTranslation('translation');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pages, setPages] = useState<number[]>([]);
  const [lastIndex, setLastIndex] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [openModalNovoModelo, setOpenModalNovoModelo] = useState<boolean>(false);
  const [listaForms, setListaForms] = useState<ChecklistType[]>([]);

  const results = useQueries({
    queries: [
      {
        queryKey: ['GET_SERVICE_TYPES'],
        queryFn: async () => {
          try {
            const token = await AttToken();
            if (!token) throw new Error('Token not found');
            if (token) {
              const body = { ...token, empresa: token.userEmpresa };
              const response: types[] = await tiposServicoService.getAllByCompany(body);
              if (!response) throw new Error('Service types not found');
              return response;
            }
          } catch (e) {
            throw new Error('Error fetching service types');
          }
        },
        retry: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
      },
      {
        queryKey: ['GET_INDEXES'],
        queryFn: async () => {
          try {
            const token = await AttToken();
            if (!token) throw new Error('Token not found');
            if (token) {
              const response: indexType = await formularioService.getChecklistIndexes(token, 10);
              if (!response) throw new Error('Indexes not found');
              return response;
            }
          } catch (e) {
            throw new Error('Error fetching indexes');
          }
        },
        retry: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
      },
    ]
  });

  const isLoading = results?.some((result) => result.isLoading);
  const isFetching = results?.some((result) => result.isFetching);
  const isSuccess = results.every((result) => result.isSuccess);
  const isError = results.some((result) => result.isError);

  useEffect(() => {
    if (isSuccess) trackEventMatomo('gestão de formulários', 'sucesso', 'carregamento', 'índices e tipos de serviço carregados');
    if (isError) trackEventMatomo('gestão de formulários', 'erro', 'carregamento', 'falha ao carregar índices ou tipos de serviço');
    if (isSuccess) {
      if (results[1].data) {
        const pagesToSet = [];
        for (let i = 1; i <= Math.ceil(results[1].data.totalItens / 10); i++) {
          pagesToSet.push(i);
        }
        setPages(pagesToSet);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);


  const { data, isLoading: loadingForms, isError: isErrorForms, isFetching: fetchingForms, isSuccess: successForms } = useQuery({
    queryKey: ['GET_CHECKLIST', lastIndex, search, results[1]?.dataUpdatedAt ?? 0],
    queryFn: async ({ queryKey }) => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        if (token) {
          const body = { ...token, empresa: token.userEmpresa, itensPerPage: 10, lastIndex: queryKey[2] ? 0 : queryKey[1] as number, filter: queryKey[2] as string };
          const response: formsResponse = await formularioService.getListV2(body);
          if (!response) throw new Error('Forms not found');
          return response.forms;
        }
      } catch (e) {
        throw new Error('Error fetching forms');
      }
    },
    enabled: results[1]?.isSuccess,
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  const loading = isLoading || loadingForms || isFetching || fetchingForms;

  if (isError) {
    toast.error(t('custom.erro_ao_buscar_dados_tente_re'), { toastId: 'custom.erro_ao_buscar_dados_tente_re' });
  }
  if (isErrorForms) {
    trackEventMatomo('gestão de formulários', 'erro', 'carregamento', 'falha ao carregar formulários');
    if (data && Object.values(data)?.length > 0) {
      toast.error(t('custom.erro_ao_buscar_modelos_de_form'), { toastId: 'custom.erro_ao_buscar_modelos_de_form' });
    } else {
      toast.info(t('custom.voc_ainda_n_o_criou_nenhum_fo'), { toastId: 'custom.voc_ainda_n_o_criou_nenhum_fo' });
    }
  }

  useEffect(() => {
    if (successForms) trackEventMatomo('gestão de formulários', 'sucesso', 'carregamento', 'formulários carregados');
    if (data && typeof data === 'object') {
      const lista: ChecklistType[] = Array.isArray(data) ? data : Object.values(data) as ChecklistType[];
      setListaForms(lista);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successForms, loadingForms, fetchingForms]);

  function serviceName(value: string) {
    if (!Array.isArray(results[0].data)) return 'Sem tipo de serviço associado';
    const service = results[0].data?.find((serviceType: types) => serviceType.tipoId === value);
    return service ? service.nome : 'Sem tipo de serviço associado';
  }

  function handleCloseModal() {
    trackEventMatomo('gestão de formulários', 'click', 'modal', openModalNovoModelo ? 'fecha criação de formulário' : 'abre criação de formulário');
    setOpenModalNovoModelo(!openModalNovoModelo);
  }

  function handlePage(_eve: React.ChangeEvent<unknown>, pageNumber: number) {
    trackEventMatomo('Gestão de formulários', 'click', 'button', 'muda página');
    const newIndex = results[1]?.data?.indexes?.[pageNumber];
    if (newIndex !== undefined) {
      setLastIndex(newIndex);
    }
    setCurrentPage(pageNumber);
  }

  return (
    <>
      {openModalNovoModelo
        && <NovoFormulario
          open={openModalNovoModelo}
          handleCloseModal={handleCloseModal} />}
      <Stack direction='column' spacing={2} alignItems='stretch' justifyContent='flex-start' >
        <Stack direction='row' width='100%' justifyContent='space-between' alignItems='center'>
          <Stack direction='row' spacing={2} alignItems='center'>
            <Button
              onClick={handleCloseModal}
              variant="contained"
              sx={{ textTransform: 'none', fontWeight: 800, fontSize: 20, backgroundColor: '#00C4A7' }}
              disabled={loading}
              size="large"
              endIcon={<AddCircleOutlineIcon />}
            >
              Novo
            </Button>
            {results[1].data && results[1]?.data?.totalItens > 1 && <Typography color='textDisabled'>
              {`${t('checklists.modelosExistentes')}: ${results[1].data ? results[1].data.totalItens : 0}`}
            </Typography>}
          </Stack>
          <Pagination
            disabled={loading || search?.length > 0}
            style={{ display: `${!pages?.length ? 'none' : ''}` }}
            showFirstButton
            showLastButton
            shape="rounded"
            count={pages?.length}
            page={currentPage}
            onChange={handlePage}
          />
        </Stack>
        <Autocomplete
          value={search ? { nome: search } : null}
          fullWidth
          loading={loading}
          autoComplete
          getOptionLabel={option => option.nome}
          getOptionKey={option => option.nome}
          isOptionEqualToValue={(option, value) => option.nome === value.nome}
          id="combo-box-demo"
          filterSelectedOptions
          disabled={loading}
          renderOption={(props, option) => (
            <li {...props} key={props.id}>
              <span>{option.nome}</span>
            </li>
          )}
          options={Array.isArray(results[1]?.data?.nomes) ? (results[1]?.data)?.nomes
            ?.map((nome: string, index: number) => (
              { nome }
            )) : []}
          renderInput={(params) => <TextField {...params} label={`${t('table.search')} ${t('notifications.unread.form')}`} />}
          onChange={(_event, newValue: { nome: string } | null) => {
            if (newValue) trackEventMatomo('gestão de formulários', 'click', 'select', 'pesquisa formulário');
            setSearch(newValue?.nome || '');
          }}
        />
        {loading ? (results[1]?.data && <CardFormularioSkeletonGrid length={results[1].data.nomes?.length} />)
          : <Grid container spacing={2} sx={{ paddingBottom: 4 }}>
            {listaForms?.map((form: ChecklistType, index: number) => (
              <Grid size={{ sm: 12, md: 6 }} key={index}>
                <CardFormulario
                  disabled={loading}
                  tipoServico={serviceName(form?.tipoServico ?? '')}
                  form={form}
                />
              </Grid>
            ))}
          </Grid>}
      </Stack>
    </>
  );
}
